import type { Rect } from "../game/input";
import { mobileFontSize } from "../game/platform";

interface ButtonStyle {
  fill: string;
  stroke: string;
  text: string;
  font: string;
  preserveFont: boolean;
  variant: "menu8bit" | "classic" | "lobby";
}

const MENU_GUIDE = {
  frame: "#090f23",
  shadow: "rgba(8,3,30,0.7)",
  bodyBlue: "#221250",
  bodyPurple: "#32145c",
  bodyTeal: "#203450",
  strokeCyan: "#69e6ff",
  strokeMagenta: "#b64ae3",
  text: "#e8f7ff"
} as const;

function menuButtonFont(): string {
  return `bold ${mobileFontSize(6)}px 'Press Start 2P', monospace`;
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number
): void {
  const r = Math.max(0, Math.min(radius, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawFittedLabel(
  ctx: CanvasRenderingContext2D,
  label: string,
  centerX: number,
  centerY: number,
  maxWidth: number
): void {
  let text = label;
  let measured = ctx.measureText(text).width;
  if (measured <= maxWidth || measured <= 0) {
    ctx.fillText(text, centerX, centerY);
    return;
  }
  while (text.length > 1 && measured > maxWidth) {
    text = text.slice(0, -1);
    measured = ctx.measureText(text).width;
  }
  ctx.fillText(text, centerX, centerY);
}

const DEFAULT_BUTTON_STYLE: ButtonStyle = {
  fill: MENU_GUIDE.bodyBlue,
  stroke: MENU_GUIDE.strokeCyan,
  text: MENU_GUIDE.text,
  font: `bold ${mobileFontSize(8)}px 'Press Start 2P', monospace`,
  preserveFont: false,
  variant: "menu8bit"
};

function harmonizeMenuFill(fill: string): string {
  switch (fill.toLowerCase()) {
    case "#1a2a4c":
    case "#1c3459":
    case "#1f2e44":
      return MENU_GUIDE.bodyBlue;
    case "#2a1a3d":
    case "#2a1f3d":
      return MENU_GUIDE.bodyPurple;
    case "#1a3340":
      return MENU_GUIDE.bodyTeal;
    default:
      return fill;
  }
}

function harmonizeMenuStroke(stroke: string): string {
  switch (stroke.toLowerCase()) {
    case "#5c8cd4":
    case "#7ca8ea":
    case "#75b5ff":
    case "#6f94c5":
    case "#64d8ff":
    case "#6ea3ff":
      return MENU_GUIDE.strokeCyan;
    case "#9975cc":
    case "#d4a8ff":
    case "#c89fff":
      return MENU_GUIDE.strokeMagenta;
    default:
      return stroke;
  }
}

export function drawButton(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  label: string,
  options?: Partial<ButtonStyle>
): void {
  const style: ButtonStyle = {
    fill: options?.fill ?? DEFAULT_BUTTON_STYLE.fill,
    stroke: options?.stroke ?? DEFAULT_BUTTON_STYLE.stroke,
    text: options?.text ?? DEFAULT_BUTTON_STYLE.text,
    font: options?.font ?? DEFAULT_BUTTON_STYLE.font,
    preserveFont: options?.preserveFont ?? DEFAULT_BUTTON_STYLE.preserveFont,
    variant: options?.variant ?? DEFAULT_BUTTON_STYLE.variant
  };

  if (style.variant === "menu8bit") {
    style.fill = harmonizeMenuFill(style.fill);
    style.stroke = harmonizeMenuStroke(style.stroke);
    if (!style.preserveFont) {
      style.font = menuButtonFont();
    }
    if (style.text === "#f2f7ff") {
      style.text = MENU_GUIDE.text;
    }
  }

  if (style.variant === "lobby") {
    const radius = 6;

    // Button background
    ctx.fillStyle = style.fill;
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, radius);
    ctx.fill();

    // Thin stroke
    ctx.strokeStyle = style.stroke;
    ctx.lineWidth = 1.5;
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, radius);
    ctx.stroke();

    // Label
    ctx.fillStyle = style.text;
    ctx.font = style.font;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    drawFittedLabel(ctx, label, rect.x + rect.w / 2, rect.y + rect.h / 2 + 1, rect.w - 10);
    return;
  }

  if (style.variant === "classic") {
    const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 680 + (rect.x + rect.y) / 80);
    const radius = 8;

    // Button shadow for depth
    ctx.fillStyle = "rgba(0,0,0,0.4)";
    roundedRectPath(ctx, rect.x + 2, rect.y + 3, rect.w, rect.h, radius);
    ctx.fill();

    // Button background
    ctx.fillStyle = style.fill;
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, radius);
    ctx.fill();

    // Inner highlight for 3D effect
    ctx.save();
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, radius);
    ctx.clip();
    ctx.fillStyle = `rgba(255,255,255,${(0.10 + pulse * 0.08).toFixed(3)})`;
    ctx.fillRect(rect.x + 2, rect.y + 2, rect.w - 4, rect.h / 3);
    ctx.restore();

    // Border
    ctx.save();
    ctx.strokeStyle = style.stroke;
    ctx.shadowColor = style.stroke;
    ctx.shadowBlur = 2 + pulse * 3;
    ctx.lineWidth = 2;
    roundedRectPath(ctx, rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2, Math.max(0, radius - 1));
    ctx.stroke();
    ctx.restore();

    // Label
    ctx.fillStyle = style.text;
    ctx.font = style.font;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    drawFittedLabel(ctx, label, rect.x + rect.w / 2, rect.y + rect.h / 2 + 1, rect.w - 10);
    return;
  }

  // 8bitcn-inspired menu button: chunky outer border + inset gloss + hard drop shadow.
  const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 760 + (rect.x * 0.08 + rect.y * 0.05));
  const px = 2;
  const radius = 9;
  const border = MENU_GUIDE.frame;
  ctx.fillStyle = MENU_GUIDE.shadow;
  roundedRectPath(ctx, rect.x + px + 1, rect.y + px + 1, rect.w, rect.h, radius);
  ctx.fill();

  // Outer frame
  ctx.fillStyle = border;
  roundedRectPath(ctx, rect.x - px, rect.y - px, rect.w + px * 2, rect.h + px * 2, radius + 2);
  ctx.fill();

  // Main body
  ctx.fillStyle = style.fill;
  roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, radius);
  ctx.fill();

  // Accent border line keeps existing color palette cues.
  ctx.save();
  ctx.strokeStyle = style.stroke;
  ctx.shadowColor = style.stroke;
  ctx.shadowBlur = 3 + pulse * 4;
  ctx.lineWidth = 1.3;
  roundedRectPath(ctx, rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2, Math.max(0, radius - 1));
  ctx.stroke();
  ctx.restore();

  // Top/left gloss and bottom/right shade for 8-bit depth.
  ctx.save();
  roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, radius);
  ctx.clip();
  ctx.fillStyle = `rgba(255,255,255,${(0.14 + pulse * 0.08).toFixed(3)})`;
  ctx.fillRect(rect.x + 3, rect.y + 3, rect.w - 6, 3);
  ctx.fillRect(rect.x + 3, rect.y + 3, 3, rect.h - 6);
  ctx.fillStyle = "rgba(0,0,0,0.2)";
  ctx.fillRect(rect.x + 3, rect.y + rect.h - 6, rect.w - 6, 3);
  ctx.fillRect(rect.x + rect.w - 6, rect.y + 3, 3, rect.h - 6);
  ctx.restore();

  // Label
  ctx.fillStyle = style.text;
  ctx.font = style.font;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  drawFittedLabel(ctx, label, rect.x + rect.w / 2, rect.y + rect.h / 2 + 1, rect.w - 10);
}
